import React, {
    useState,
    useMemo,
    useRef,
    useImperativeHandle,
    forwardRef
} from 'react';
import { Oval } from 'react-loader-spinner';
import { TFunction } from 'i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactTable, { Column, RowInfo } from 'react-table';
import 'react-table/react-table.css';
import { useHistory } from 'react-router-dom';
import {
    IdashboardAnalyticsData,
    IanalyticsInspectionDetail,
    IanalyticsInspectionDetailLocation,
    IanalyticsInspectionDetailProduct
} from '../../models';
import { ProductDisplayCategory } from '../../models-enums';
import { setTableFilter } from '../../actions/manageInventoryActions';

/**
 * API Response Structure from /update/dashboard endpoint:
 * Uses IdashboardAnalyticsData which includes:
 * {
 *   inspection: IanalyticsInspection[],
 *   inspectionDetail: IanalyticsInspectionDetail[],
 *   pmp: IanalyticsPMP,
 *   smartlinks: IanalyticsTAEDashboard[],
 *   quote: IanalyticsQuote
 * }
 */

interface IStatusGroup {
    status: string;
    statusLabel: string;
    count: number;
    nodes: ILocationNode[];
}

// Tree of building -> floor -> area -> room; positions roll up into their room (MMG-4339)
interface ILocationNode {
    key: string;
    id: string; // Level entity ID so same-named locations never merge
    name: string;
    depth: number;
    totalCount: number;
    categoryColumns: { [categoryId: number]: number }; // Count per ProductDisplayCategory enum
    children: ILocationNode[];
    products: IanalyticsInspectionDetailProduct[];
}

interface ILocationSegment {
    id: string;
    name: string;
}

interface AssetOverviewProps {
    data: IdashboardAnalyticsData | null | undefined;
    isLoading: boolean;
    searchTerm: string;
    selectedStatus: string;
    selectedLocation: string;
    selectedCategory: string;
    t: TFunction;
    setTableFilter: typeof setTableFilter;
}

export interface AssetOverviewHandle {
    scrollToAndExpandStatus: (statusName: string) => void;
}

// Category columns mapped to ProductDisplayCategory enum values
const CATEGORY_COLUMNS: Array<{ id: number; name: string }> = [
    { id: ProductDisplayCategory.MasterAlarms, name: 'Master Alarms' },
    { id: ProductDisplayCategory.SourceSys, name: 'Source Sys' },
    {
        id: ProductDisplayCategory.ManifoldBulkSysEsc,
        name: 'Manifold / Bulk Sys / ESC'
    },
    {
        id: ProductDisplayCategory.ZVBAreaComboAlarmValves,
        name: 'ZVB / Area & Combo Alms / Valves / AVSU'
    },
    { id: ProductDisplayCategory.Outlet, name: 'TU / Outlets' },
    { id: ProductDisplayCategory.Other, name: 'Other' }
];

// Helper to get status label
const getStatusLabel = (status: string, t: TFunction): string => {
    // Map status strings to display labels
    const statusLabels: { [key: string]: string } = {
        pass: 'Pass',
        fail: 'Compliance Required',
        repaired: 'Repaired',
        notTested: 'Not Tested',
        cannotComplete: 'Cannot Complete'
    };
    return statusLabels[status] || status;
};

// Helper to get status CSS class
const getStatusClass = (status: string): string => {
    return `status-${status.toLowerCase()}`;
};

const UNALLOCATED = '(unallocated)';

// Location levels for one grouping row, building -> floor -> area -> room
const getLocationSegments = (
    location: IanalyticsInspectionDetailLocation
): ILocationSegment[] => {
    const levels = [
        { id: location.buildingID, name: location.buildingName },
        { id: location.floorID, name: location.floorName },
        { id: location.locationID, name: location.locationName },
        { id: location.roomID, name: location.roomName }
    ];

    const segments: ILocationSegment[] = [];
    levels.forEach(level => {
        if (level.name && level.name.trim() !== '') {
            segments.push({ id: level.id || level.name, name: level.name });
        }
    });

    if (segments.length > 0) {
        return segments;
    }

    // Fallback for responses without the structured level names
    return location.fullLocation && location.fullLocation !== UNALLOCATED
        ? [{ id: location.fullLocation, name: location.fullLocation }]
        : [{ id: UNALLOCATED, name: UNALLOCATED }];
};

const compareNodes = (a: ILocationNode, b: ILocationNode): number => {
    if (a.name === UNALLOCATED) return 1;
    if (b.name === UNALLOCATED) return -1;
    return a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: 'base'
    });
};

const sortTree = (nodes: ILocationNode[]): void => {
    nodes.sort(compareNodes);
    nodes.forEach(node => sortTree(node.children));
};

/**
 * Process inspectionDetail from dashboard API data into hierarchical structure
 * Uses ProductDisplayCategory enum for column mapping
 */
const processInspectionData = (
    data: IdashboardAnalyticsData,
    t: TFunction
): {
    statusGroups: IStatusGroup[];
    totalCount: number;
} => {
    if (!data || !data.inspectionDetail || data.inspectionDetail.length === 0) {
        return { statusGroups: [], totalCount: 0 };
    }

    let totalCount = 0;

    // Get list of valid category IDs (excluding "Other")
    const validCategoryIds = CATEGORY_COLUMNS.filter(
        cat => cat.name !== 'Other'
    ).map(cat => cat.id);
    const otherCategoryId = ProductDisplayCategory.Other;

    const statusGroups: IStatusGroup[] = data.inspectionDetail.map(detail => {
        const rootNodes: ILocationNode[] = [];
        let statusTotal = 0;

        detail.locations.forEach(location => {
            const segments = getLocationSegments(location);

            // Walk/create the node path, rolling counts up into every level
            let siblings = rootNodes;
            let node: ILocationNode | null = null;
            let path = detail.status;

            segments.forEach((segment, depth) => {
                path += `:${segment.id}`;
                const found = siblings.find(n => n.id === segment.id);
                const current: ILocationNode = found || {
                    key: path,
                    id: segment.id,
                    name: segment.name,
                    depth,
                    totalCount: 0,
                    categoryColumns: {},
                    children: [],
                    products: []
                };
                if (!found) {
                    siblings.push(current);
                }

                location.products.forEach(product => {
                    const categoryId = validCategoryIds.includes(
                        product.productDisplayCategory
                    )
                        ? product.productDisplayCategory
                        : otherCategoryId;
                    current.categoryColumns[categoryId] =
                        (current.categoryColumns[categoryId] || 0) +
                        product.count;
                    current.totalCount += product.count;
                });

                siblings = current.children;
                node = current;
            });

            if (node !== null) {
                const leaf: ILocationNode = node;
                leaf.products = leaf.products.concat(location.products);
            }

            const locationTotal = location.products.reduce(
                (sum, product) => sum + product.count,
                0
            );
            statusTotal += locationTotal;
            totalCount += locationTotal;
        });

        sortTree(rootNodes);

        return {
            status: detail.status,
            statusLabel: getStatusLabel(detail.status, t),
            count: statusTotal,
            nodes: rootNodes
        };
    });

    return {
        statusGroups,
        totalCount
    };
};

const AssetOverview = forwardRef<AssetOverviewHandle, AssetOverviewProps>(
    (
        {
            data,
            isLoading,
            searchTerm,
            selectedStatus,
            selectedLocation,
            selectedCategory,
            t,
            setTableFilter: setFilter
        },
        ref
    ) => {
        const history = useHistory();
        const [isCollapsed, setIsCollapsed] = useState(false);
        const [expandedStatuses, setExpandedStatuses] = useState<{
            [key: string]: boolean;
        }>({});
        const [expandedLocations, setExpandedLocations] = useState<{
            [key: string]: boolean;
        }>({});
        const containerRef = useRef<HTMLDivElement>(null);
        const statusSectionRefs = useRef<{
            [key: string]: HTMLDivElement | null;
        }>({});

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            scrollToAndExpandStatus: (statusName: string) => {
                // Ensure the section is not collapsed
                if (isCollapsed) {
                    setIsCollapsed(false);
                }

                // Expand the status
                setExpandedStatuses(prev => ({
                    ...prev,
                    [statusName]: true
                }));

                // Scroll to the component, then to the specific status section
                setTimeout(() => {
                    // First scroll to the container
                    if (containerRef.current) {
                        containerRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    }

                    // Then scroll to the specific status section
                    setTimeout(() => {
                        const statusSection =
                            statusSectionRefs.current[statusName];
                        if (statusSection) {
                            statusSection.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                            });
                        }
                    }, 300);
                }, 100);
            }
        }));

        // Filter data based on search term, selected status, location, and category
        const filteredData = useMemo(() => {
            if (!data || !data.inspectionDetail) return data;

            let filteredDetails = data.inspectionDetail;

            // Filter by status
            if (selectedStatus !== 'All') {
                filteredDetails = filteredDetails.filter(
                    detail => detail.status === selectedStatus
                );
            }

            // Filter by location or search term
            if (
                selectedLocation !== 'All' ||
                (searchTerm && searchTerm.trim() !== '')
            ) {
                const searchLower = searchTerm?.toLowerCase() || '';

                filteredDetails = filteredDetails
                    .map(detail => {
                        const filteredLocations = detail.locations
                            .map(location => {
                                let filteredProducts = location.products;

                                // Filter by location
                                if (
                                    selectedLocation !== 'All' &&
                                    location.fullLocation !== selectedLocation
                                ) {
                                    return null;
                                }

                                // Filter by search term
                                if (searchTerm && searchTerm.trim() !== '') {
                                    filteredProducts = filteredProducts.filter(
                                        product =>
                                            product.productName
                                                .toLowerCase()
                                                .includes(searchLower) ||
                                            location.fullLocation
                                                .toLowerCase()
                                                .includes(searchLower)
                                    );
                                }

                                if (filteredProducts.length === 0) return null;

                                return {
                                    ...location,
                                    products: filteredProducts
                                };
                            })
                            .filter(
                                loc => loc !== null
                            ) as IanalyticsInspectionDetailLocation[];

                        if (filteredLocations.length === 0) return null;

                        return {
                            ...detail,
                            locations: filteredLocations
                        };
                    })
                    .filter(
                        detail => detail !== null
                    ) as IanalyticsInspectionDetail[];
            }

            return {
                ...data,
                inspectionDetail: filteredDetails
            };
            // eslint-disable-next-line
        }, [
            data,
            searchTerm,
            selectedStatus,
            selectedLocation,
            selectedCategory
        ]);

        const { statusGroups, totalCount } = useMemo(() => {
            if (!filteredData)
                return {
                    statusGroups: [],
                    totalCount: 0
                };
            return processInspectionData(filteredData, t);
        }, [filteredData, t]);

        // Generate dynamic columns for ReactTable (location and product rows)
        const getDynamicColumns = (): Column[] => {
            const cols: Column[] = [
                {
                    Header: '',
                    accessor: 'location',
                    minWidth: 300,
                    Cell: (row: any) => {
                        const isLocation = row.original.isLocationRow;

                        if (isLocation) {
                            return (
                                <div
                                    className="location-row-cell"
                                    style={{
                                        paddingLeft: row.original.depth * 20
                                    }}
                                >
                                    <button
                                        className="location-toggle"
                                        onClick={() =>
                                            setExpandedLocations(prev => ({
                                                ...prev,
                                                [row.original
                                                    .locationKey]: !prev[
                                                    row.original.locationKey
                                                ]
                                            }))
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                expandedLocations[
                                                    row.original.locationKey
                                                ]
                                                    ? ['fas', 'chevron-down']
                                                    : ['fas', 'chevron-right']
                                            }
                                            className="chevron-icon"
                                        />
                                        {row.original.name}
                                    </button>
                                </div>
                            );
                        }

                        // Product row
                        return (
                            <div
                                className="asset-row-cell"
                                style={{
                                    paddingLeft: 24 + row.original.depth * 20
                                }}
                            >
                                <button
                                    type="button"
                                    className="asset-name-link"
                                    onClick={() => {
                                        setFilter({
                                            search: row.original.productName,
                                            page: 0
                                        });
                                        history.push('/inventory', {
                                            autoExpand: true
                                        });
                                    }}
                                    aria-label={`View ${row.original.productName} in inventory`}
                                >
                                    {row.original.productName}
                                </button>
                            </div>
                        );
                    }
                }
            ];

            // Rolled-up total per location level, right before the category columns
            cols.push({
                Header: '',
                accessor: 'totalCount',
                width: 50,
                Cell: (row: any) =>
                    row.original.isLocationRow ? (
                        <span className="location-count">
                            {row.original.totalCount}
                        </span>
                    ) : null
            });

            // Get list of valid category IDs (excluding "Other")
            const validCategoryIds = CATEGORY_COLUMNS.filter(
                cat => cat.name !== 'Other'
            ).map(cat => cat.id);
            const otherCategoryId = ProductDisplayCategory.Other;

            // Add columns for each category
            CATEGORY_COLUMNS.forEach((category, index) => {
                cols.push({
                    Header: category.name,
                    accessor: `col_${index}`,
                    width: 115,
                    Cell: (row: any) => {
                        if (row.original.isLocationRow) {
                            // Check if this location is expanded
                            const isExpanded =
                                expandedLocations[row.original.locationKey];

                            // Only show counts when location is NOT expanded
                            if (!isExpanded) {
                                const count =
                                    row.original.categoryColumns?.[category.id];
                                return count ? <span>{count}</span> : null;
                            }
                            return null;
                        }
                        // Product row - determine which category this product belongs to
                        const productCategoryId =
                            row.original.productDisplayCategory;

                        // Check if this category is in our list, otherwise it goes to "Other"
                        const effectiveCategoryId = validCategoryIds.includes(
                            productCategoryId
                        )
                            ? productCategoryId
                            : otherCategoryId;

                        // Show count only if it matches this column's category
                        return effectiveCategoryId === category.id ? (
                            <span>{row.original.count}</span>
                        ) : null;
                    }
                });
            });

            return cols;
        };

        // Generate table data for a specific status group (visible tree rows and products)
        const getStatusTableData = (statusGroup: IStatusGroup) => {
            const rows: any[] = [];

            const addNode = (node: ILocationNode) => {
                rows.push({
                    isLocationRow: true,
                    locationKey: node.key,
                    name: node.name,
                    depth: node.depth,
                    totalCount: node.totalCount,
                    categoryColumns: node.categoryColumns,
                    location: node.name
                });

                // Add child levels and product rows if this level is expanded
                if (expandedLocations[node.key]) {
                    node.children.forEach(addNode);
                    node.products.forEach(product => {
                        rows.push({
                            ...product,
                            isProductRow: true,
                            depth: node.depth + 1,
                            location: product.productName
                        });
                    });
                }
            };

            statusGroup.nodes.forEach(addNode);

            return rows;
        };

        const hasData = statusGroups.length > 0;

        if (isLoading) {
            return (
                <div className="asset-details-view" ref={containerRef}>
                    <div className="asset-details-header">
                        <h3 className="asset-details-title">Asset Overview</h3>
                    </div>
                    <div className="loading-container">
                        <Oval
                            color="#ff9738"
                            secondaryColor="#ffcd9e"
                            height={40}
                            width={40}
                        />
                    </div>
                </div>
            );
        }

        if (!hasData) {
            return null;
        }

        return (
            <div className="asset-details-view" ref={containerRef}>
                <div className="asset-details-header">
                    <h3 className="asset-details-title">Asset Overview</h3>
                    <div className="asset-details-controls">
                        <span className="asset-count-badge">{totalCount}</span>
                        <button
                            className="collapse-button"
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            aria-label={
                                isCollapsed
                                    ? 'Expand section'
                                    : 'Collapse section'
                            }
                        >
                            {isCollapsed ? 'Expand' : 'Collapse'}
                            <FontAwesomeIcon
                                icon={
                                    isCollapsed
                                        ? ['fas', 'chevron-down']
                                        : ['fas', 'chevron-up']
                                }
                            />
                        </button>
                    </div>
                </div>

                {!isCollapsed && (
                    <div className="asset-details-table-container">
                        {statusGroups.map(statusGroup => (
                            <div
                                key={statusGroup.status}
                                className="status-section"
                                ref={el =>
                                    (statusSectionRefs.current[
                                        statusGroup.status
                                    ] = el)
                                }
                            >
                                {/* Combined header: status info + column headers */}
                                <div className="combined-header-row">
                                    <div className="status-header">
                                        <span
                                            className="status-toggle"
                                            onClick={() =>
                                                setExpandedStatuses(prev => ({
                                                    ...prev,
                                                    [statusGroup.status]: !prev[
                                                        statusGroup.status
                                                    ]
                                                }))
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    expandedStatuses[
                                                        statusGroup.status
                                                    ]
                                                        ? [
                                                              'fas',
                                                              'chevron-down'
                                                          ]
                                                        : [
                                                              'fas',
                                                              'chevron-right'
                                                          ]
                                                }
                                                className="chevron-icon"
                                            />
                                        </span>
                                        <span
                                            className={`status-badge ${getStatusClass(
                                                statusGroup.status
                                            )}`}
                                        >
                                            {statusGroup.statusLabel}
                                        </span>
                                        <span className="count-badge">
                                            {statusGroup.count}
                                        </span>
                                    </div>
                                    <div
                                        className={`column-headers ${
                                            !expandedStatuses[
                                                statusGroup.status
                                            ]
                                                ? 'collapsed'
                                                : ''
                                        }`}
                                    >
                                        {CATEGORY_COLUMNS.map(
                                            (category, index) => (
                                                <div
                                                    key={index}
                                                    className="column-header"
                                                >
                                                    {category.name}
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>

                                {/* ReactTable for this status (only if expanded) */}
                                {expandedStatuses[statusGroup.status] && (
                                    <ReactTable
                                        data={getStatusTableData(statusGroup)}
                                        columns={getDynamicColumns()}
                                        showPagination={false}
                                        sortable={false}
                                        resizable={false}
                                        minRows={0}
                                        pageSize={
                                            getStatusTableData(statusGroup)
                                                .length
                                        }
                                        className="asset-details-table -highlight -hide-header"
                                        noDataText="No data available"
                                        getTrProps={(
                                            state: any,
                                            rowInfo: RowInfo | undefined
                                        ) => {
                                            if (!rowInfo) return {};
                                            return {
                                                className: rowInfo.original
                                                    .isLocationRow
                                                    ? 'location-row'
                                                    : 'asset-row'
                                            };
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
);

AssetOverview.displayName = 'AssetOverview';

export default AssetOverview;
